import { Controller } from "@hotwired/stimulus"

// Chart.js is loaded the first time a chart connects, so it stays out of the bundle every page downloads.
let chartModule
const loadChart = () => (chartModule ||= import("chart.js").then((module) => {
  module.Chart.register(
    module.BarController,
    module.LineController,
    module.PieController,
    module.DoughnutController,
    module.CategoryScale,
    module.LinearScale,
    module.BarElement,
    module.ArcElement,
    module.LineElement,
    module.PointElement,
    module.Tooltip,
    module.Legend
  )
  return module.Chart
}))

const VALUE_LABEL_MIN_SEGMENT_SIZE = 28
const VALUE_LABEL_OUTSIDE_OFFSET = 8
const VALUE_LABEL_FONT_SIZE = 12
const VALUE_LABEL_SMALL_FONT_SIZE = 10
const VALUE_LABEL_MIN_ARC_ANGLE = 0.1
const TOOLTIP_LINE_MAX_LENGTH = 48

export default class extends Controller {
  static values = {
    config: Object
  }

  async connect () {
    const connection = (this.connection = Symbol("connection"))
    if (!this.configValue?.chartdata) return

    const Chart = await loadChart()
    if (this.connection !== connection) return // disconnected (or reconnected) while loading

    const context = this.element.getContext("2d")
    if (!context) return

    const chartType = this.configValue.charttype || "bar"
    const chartOptions = this.configValue.chartoptions || {}
    const language = this.configValue.language || chartOptions.locale || "fr-FR"

    chartOptions.locale = language

    this.applyTooltipFormatting(chartOptions, language, chartType)
    this.applyPercentageAxisTicks(chartOptions, language, chartType)
    this.applyIntegerAxisTicks(chartOptions, chartType)
    const plugins = this.buildPlugins(chartOptions, language, chartType)

    this.chart = new Chart(context, {
      type: chartType,
      data: this.configValue.chartdata,
      options: chartOptions,
      plugins
    })

    this.fitCircularChartToContentIfNeeded(chartOptions, chartType)
  }

  disconnect () {
    this.connection = null
    if (this.chart) {
      this.chart.destroy()
    }
  }

  fitCircularChartToContentIfNeeded (chartOptions, chartType) {
    if (!chartOptions?.fit_to_content) return
    if (chartType !== "pie" && chartType !== "doughnut") return

    requestAnimationFrame(() => this.fitCircularChartToContent())
  }

  fitCircularChartToContent () {
    if (!this.chart) return

    const container = this.chart.canvas.parentElement
    if (!container) return

    const chart = this.chart
    const { chartArea, legend, width } = chart
    if (!legend?.width) return

    const areaWidth = chartArea.right - chartArea.left
    const areaHeight = chartArea.bottom - chartArea.top
    const pieSize = Math.min(areaWidth, areaHeight)
    const gap = Math.max(0, legend.left - chartArea.right)
    const rightPad = Math.max(0, width - legend.right)
    const legendWidth = Math.ceil(legend.width)
    // Keep the full multi-column legend; only remove slack around the pie.
    const neededWidth = Math.ceil(chartArea.left + pieSize + gap + legendWidth + rightPad + 12)

    if (container.clientWidth - neededWidth < 2) return

    chart.options.plugins ||= {}
    chart.options.plugins.legend ||= {}
    // Reserve the measured 2-column legend width on the next layout.
    chart.options.plugins.legend.maxWidth = legendWidth
    container.style.width = `${neededWidth}px`
    chart.resize()
  }

  applyTooltipFormatting (chartOptions, language, chartType) {
    const tooltipOptions = chartOptions?.plugins?.tooltip
    if (!tooltipOptions) return

    if (tooltipOptions.label_only) {
      tooltipOptions.displayColors = false
      tooltipOptions.callbacks = {
        title: () => "",
        label: (context) => context.label || ""
      }
      return
    }

    if (!tooltipOptions.values_only_with_percentage) return

    const percentageLabels = Boolean(chartOptions.plugins?.percentage_labels)
    const numberFormatter = this.numberFormatter(language, {
      fixedFractionDigits: percentageLabels
        ? this.percentageFractionDigits(chartOptions, chartType)
        : null
    })
    const details = tooltipOptions.details || []
    const circularChart = chartType === "doughnut" || chartType === "pie"

    tooltipOptions.displayColors = false
    tooltipOptions.callbacks = {
      title: (items) => {
        const detail = details[items[0]?.dataIndex]
        const label = items[0]?.label || ""

        if (circularChart && detail && this.tooltipLineLength(label, "99,9", detail) > TOOLTIP_LINE_MAX_LENGTH) {
          return label
        }
        if (!percentageLabels || circularChart) return ""

        return label
      },
      label: (context) => {
        const label = percentageLabels && !circularChart
          ? (context.dataset?.label || "")
          : (context.label || "")
        const value = this.parsedValue(context)
        const detail = details[context.dataIndex]
        const percentageText = `${numberFormatter.format(value)}%`
        const singleLine = detail
          ? `${label} : ${percentageText} (${detail})`
          : `${label} : ${percentageText}`

        if (!detail || singleLine.length <= TOOLTIP_LINE_MAX_LENGTH) return singleLine

        const percentageLine = circularChart ? percentageText : `${label} : ${percentageText}`
        return [percentageLine, ...this.wrapTooltipText(`(${detail})`)]
      }
    }
  }

  tooltipLineLength (label, percentageText, detail) {
    return `${label} : ${percentageText}% (${detail})`.length
  }

  wrapTooltipText (text, maxLength = TOOLTIP_LINE_MAX_LENGTH) {
    const normalized = String(text).trim()
    if (!normalized) return []
    if (normalized.length <= maxLength) return [normalized]

    const tokens = normalized.split(/(?<=,)\s+|\s+/).filter(Boolean)
    const lines = []
    let currentLine = ""

    tokens.forEach((token) => {
      const candidate = currentLine ? `${currentLine} ${token}` : token
      if (candidate.length <= maxLength) {
        currentLine = candidate
        return
      }

      if (currentLine) lines.push(currentLine)

      if (token.length <= maxLength) {
        currentLine = token
        return
      }

      let remaining = token
      while (remaining.length > maxLength) {
        lines.push(remaining.slice(0, maxLength))
        remaining = remaining.slice(maxLength)
      }
      currentLine = remaining
    })

    if (currentLine) lines.push(currentLine)
    return lines
  }

  applyPercentageAxisTicks (chartOptions, language, chartType) {
    if (!chartOptions?.plugins?.percentage_labels) return
    if (chartType === "doughnut" || chartType === "pie") return

    const numberFormatter = this.numberFormatter(language, {
      fixedFractionDigits: this.percentageFractionDigits(chartOptions, chartType)
    })
    const valueAxis = this.valueAxisKey(chartOptions)

    chartOptions.scales ||= {}
    chartOptions.scales[valueAxis] ||= {}
    chartOptions.scales[valueAxis].ticks ||= {}
    chartOptions.scales[valueAxis].ticks.callback = (value) => {
      return `${numberFormatter.format(Number(value) || 0)}%`
    }
  }

  applyIntegerAxisTicks (chartOptions, chartType) {
    if (!chartOptions?.scales) return
    if (chartType === "doughnut" || chartType === "pie") return
    if (chartOptions?.plugins?.percentage_labels) return

    const valueAxis = this.valueAxisKey(chartOptions)
    const axis = chartOptions.scales[valueAxis]
    const ticks = axis?.ticks
    if (!ticks) return

    const stepSize = Number(ticks.stepSize)
    const precision = ticks.precision
    const forceInteger = precision === 0 || (Number.isFinite(stepSize) && stepSize >= 1)
    if (!forceInteger) return

    ticks.stepSize = Math.max(Number.isFinite(stepSize) ? stepSize : 1, 1)
    ticks.precision = 0
    ticks.callback = (value) => (Number.isInteger(value) ? value : undefined)
  }

  buildPlugins (chartOptions, language, chartType) {
    if (!chartOptions?.plugins?.value_labels && !chartOptions?.plugins?.percentage_labels) return []

    const asPercentage = Boolean(chartOptions.plugins?.percentage_labels)
    const numberFormatter = this.numberFormatter(language, {
      fixedFractionDigits: asPercentage
        ? this.percentageFractionDigits(chartOptions, chartType)
        : null
    })
    const circularChart = chartType === "doughnut" || chartType === "pie"

    return [{
      id: "chartValueLabels",
      afterDatasetsDraw: (chart) => {
        const { ctx } = chart
        const horizontal = chart.options.indexAxis === "y"

        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex)
          if (!meta || meta.hidden) return

          meta.data.forEach((element, index) => {
            const value = Number(dataset.data[index]) || 0
            if (value <= 0) return

            const formattedValue = numberFormatter.format(value)
            const text = asPercentage ? `${formattedValue}%` : formattedValue
            const fillStyle = this.labelTextColor(dataset, index)

            if (circularChart) {
              this.drawArcValueLabel(ctx, element, text, fillStyle)
            } else {
              this.drawBarValueLabel(ctx, element, text, fillStyle, horizontal)
            }
          })
        })
      }
    }]
  }

  drawBarValueLabel (ctx, element, text, fillStyle, horizontal) {
    const props = element.getProps(["x", "y", "base"], true)
    const segmentSize = horizontal
      ? Math.abs(props.x - props.base)
      : Math.abs(props.y - props.base)
    const outside = segmentSize < VALUE_LABEL_MIN_SEGMENT_SIZE

    let textX
    let textY
    let textAlign = "center"
    let labelFillStyle = fillStyle
    let fontSize = VALUE_LABEL_FONT_SIZE

    if (outside) {
      fontSize = VALUE_LABEL_SMALL_FONT_SIZE
      labelFillStyle = "#3c3c3c"

      if (horizontal) {
        textX = Math.max(props.x, props.base) + VALUE_LABEL_OUTSIDE_OFFSET
        textY = props.y
        textAlign = "left"
      } else {
        textX = props.x
        textY = Math.min(props.y, props.base) - VALUE_LABEL_OUTSIDE_OFFSET
      }
    } else {
      textX = horizontal ? (props.x + props.base) / 2 : props.x
      textY = horizontal ? props.y : (props.y + props.base) / 2
    }

    this.drawValueText(ctx, text, textX, textY, labelFillStyle, { fontSize, textAlign })
  }

  drawArcValueLabel (ctx, element, text, fillStyle) {
    const props = element.getProps(
      ["x", "y", "startAngle", "endAngle", "innerRadius", "outerRadius"],
      true
    )
    const angleSpan = Math.abs(props.endAngle - props.startAngle)
    if (angleSpan < VALUE_LABEL_MIN_ARC_ANGLE) return

    const midAngle = (props.startAngle + props.endAngle) / 2
    const radius = (props.innerRadius + props.outerRadius) / 2
    const textX = props.x + Math.cos(midAngle) * radius
    const textY = props.y + Math.sin(midAngle) * radius

    this.drawValueText(ctx, text, textX, textY, fillStyle)
  }

  drawValueText (ctx, text, textX, textY, fillStyle, { fontSize = VALUE_LABEL_FONT_SIZE, textAlign = "center" } = {}) {
    ctx.save()
    ctx.fillStyle = fillStyle
    ctx.font = `${fontSize}px sans-serif`
    ctx.textAlign = textAlign
    ctx.textBaseline = "middle"
    ctx.fillText(text, textX, textY)
    ctx.restore()
  }

  labelTextColor (dataset, index) {
    const backgroundColor = Array.isArray(dataset.backgroundColor)
      ? dataset.backgroundColor[index]
      : dataset.backgroundColor

    return this.isDarkColor(backgroundColor) ? "#ffffff" : "#3c3c3c"
  }

  isDarkColor (color) {
    if (typeof color !== "string") return false

    const hex = color.trim()
    const shortHexMatch = hex.match(/^#([0-9a-f]{3})$/i)
    const longHexMatch = hex.match(/^#([0-9a-f]{6})$/i)
    let red
    let green
    let blue

    if (shortHexMatch) {
      red = parseInt(shortHexMatch[1][0] + shortHexMatch[1][0], 16)
      green = parseInt(shortHexMatch[1][1] + shortHexMatch[1][1], 16)
      blue = parseInt(shortHexMatch[1][2] + shortHexMatch[1][2], 16)
    } else if (longHexMatch) {
      red = parseInt(longHexMatch[1].slice(0, 2), 16)
      green = parseInt(longHexMatch[1].slice(2, 4), 16)
      blue = parseInt(longHexMatch[1].slice(4, 6), 16)
    } else {
      return false
    }

    const luminance = ((0.299 * red) + (0.587 * green) + (0.114 * blue)) / 255
    return luminance < 0.55
  }

  numberFormatter (language, { fixedFractionDigits = null } = {}) {
    if (fixedFractionDigits == null) {
      return new Intl.NumberFormat(language)
    }

    return new Intl.NumberFormat(language, {
      minimumFractionDigits: fixedFractionDigits,
      maximumFractionDigits: fixedFractionDigits
    })
  }

  percentageFractionDigits (chartOptions, chartType) {
    if (chartType === "doughnut" || chartType === "pie") return 1

    const precision = chartOptions?.scales?.[this.valueAxisKey(chartOptions)]?.ticks?.precision
    return Number.isInteger(precision) ? precision : 1
  }

  valueAxisKey (chartOptions) {
    return chartOptions.indexAxis === "y" ? "x" : "y"
  }

  parsedValue (context) {
    if (context.raw != null && typeof context.raw !== "object") {
      return Number(context.raw) || 0
    }

    if (typeof context.parsed === "number") return context.parsed || 0

    if (context.parsed && typeof context.parsed === "object") {
      const horizontal = context.chart?.options?.indexAxis === "y"
      const value = horizontal ? context.parsed.x : context.parsed.y
      return Number(value) || 0
    }

    return 0
  }
}
